import {
  Box,
  Text,
  Group,
  ThemeIcon,
  useMantineTheme,
  RingProgress,
} from '@mantine/core';
import { forwardRef, useMemo } from 'react';
import { IconArrowUpRight, IconArrowDownRight, IconMinus } from '@tabler/icons-react';
import { Paper } from '../../ui';
import { getContrastingTextColor } from '../../../utils/color';

export interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    label?: string;
  };
  progress?: {
    value: number;
    label?: string;
  };
  variant?: 'default' | 'gradient' | 'outlined' | 'minimal';
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
}

const StatCard = forwardRef<HTMLDivElement, StatCardProps>(
  (
    {
      title,
      value,
      description,
      icon,
      trend,
      progress,
      variant = 'default',
      gradientVariant = 'primary',
      size = 'md',
      onClick,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];

    // Calculate text color for gradient background
    const gradientBg = `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    const sizes = {
      sm: { padding: 'sm', titleSize: 'xs', valueSize: 'lg', iconSize: 32 },
      md: { padding: 'md', titleSize: 'sm', valueSize: 'xl', iconSize: 40 },
      lg: { padding: 'lg', titleSize: 'md', valueSize: '2rem', iconSize: 48 },
    };

    const currentSize = sizes[size];

    const getTrendIcon = () => {
      if (!trend) return null;
      if (trend.value > 0) return <IconArrowUpRight size={16} />;
      if (trend.value < 0) return <IconArrowDownRight size={16} />;
      return <IconMinus size={16} />;
    };

    const getTrendColor = () => {
      if (!trend) return 'gray';
      if (trend.value > 0) return 'teal';
      if (trend.value < 0) return 'red';
      return 'gray';
    };

    const paperVariant = variant === 'gradient' ? 'gradient' : variant === 'outlined' ? 'outlined' : 'default';
    const isGradient = variant === 'gradient';

    return (
      <Paper
        ref={ref}
        variant={paperVariant}
        gradientVariant={gradientVariant}
        gradientOpacity={isGradient ? 1 : 0.1}
        p={currentSize.padding}
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      >
        <Group justify="space-between" align="flex-start">
          <Box>
            <Text
              size={currentSize.titleSize as any}
              c={variant === 'gradient' ? autoTextColor : 'dimmed'}
              tt="uppercase"
              fw={700}
            >
              {title}
            </Text>
            <Text
              size={currentSize.valueSize as any}
              fw={700}
              mt="xs"
              c={variant === 'gradient' ? autoTextColor : undefined}
            >
              {value}
            </Text>
            {description && (
              <Text
                size="xs"
                c={variant === 'gradient' ? autoTextColor : 'dimmed'}
                style={{ opacity: variant === 'gradient' ? 0.8 : 1 }}
                mt={4}
              >
                {description}
              </Text>
            )}
          </Box>

          {icon && !progress && (
            <ThemeIcon
              size={currentSize.iconSize}
              radius="md"
              variant={variant === 'gradient' ? 'transparent' : 'light'}
              style={{
                background: variant === 'gradient' ? 'rgba(255,255,255,0.2)' : undefined,
                color: variant === 'gradient' ? autoTextColor : undefined,
              }}
            >
              {icon}
            </ThemeIcon>
          )}

          {progress && (
            <RingProgress
              size={currentSize.iconSize + 20}
              thickness={4}
              roundCaps
              sections={[
                {
                  value: progress.value,
                  color: variant === 'gradient' ? autoTextColor : gradient?.from || 'blue',
                },
              ]}
              label={
                <Text
                  size="xs"
                  ta="center"
                  c={variant === 'gradient' ? autoTextColor : undefined}
                >
                  {progress.label || `${progress.value}%`}
                </Text>
              }
            />
          )}
        </Group>

        {trend && (
          <Group gap={4} mt="md">
            <Text
              size="sm"
              c={variant === 'gradient' ? autoTextColor : getTrendColor()}
              fw={500}
            >
              <Group gap={4}>
                {getTrendIcon()}
                {Math.abs(trend.value)}%
              </Group>
            </Text>
            {trend.label && (
              <Text
                size="xs"
                c={variant === 'gradient' ? autoTextColor : 'dimmed'}
                style={{ opacity: variant === 'gradient' ? 0.7 : 1 }}
              >
                {trend.label}
              </Text>
            )}
          </Group>
        )}
      </Paper>
    );
  }
);

StatCard.displayName = 'StatCard';

export default StatCard;
